/**
 * <LanguageAutoSuggest /> — one-time non-intrusive banner offering the
 * visitor's regional language on their first visit.
 *
 * Trigger conditions (all must be true):
 *   • No language explicitly saved in localStorage (`sp:pref-lang`).
 *   • The user's locality (from LocalityContext) resolves to a language
 *     that isn't English AND isn't their browser default.
 *   • They haven't dismissed / accepted a prior suggestion.
 *
 * Rendering: a soft toast-style card in the top-right corner. Two buttons:
 *   [Read in हिन्दी]  [No thanks]
 * "Read in" applies the suggestion + closes; "No thanks" sets a dismissed
 * flag so we never nag them again on this device.
 */
import { useEffect, useState } from "react";
import { Languages, X } from "lucide-react";
import { useLocality } from "@/contexts/LocalityContext";
import { PREF_LANG_KEY, PREF_LANG_EVENT } from "@/components/LanguageChip";
import { suggestLangForRegion, nativeNameFor } from "@/lib/regionLanguage";

const DISMISS_KEY = "sp:lang-auto-dismissed";

export default function LanguageAutoSuggest() {
  const { locality } = useLocality();
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    // Delay by ~800ms so the banner never fights with page-first-paint or the
    // cookie banner. Also gives locality time to hydrate.
    const t = setTimeout(() => {
      try {
        if (localStorage.getItem(DISMISS_KEY)) return;
        const existing = localStorage.getItem(PREF_LANG_KEY);
        if (existing && existing !== "auto") return; // already chose
      } catch { /* private mode, fall through */ }
      if (!locality || (!locality.country && !locality.state)) return;
      const lang = suggestLangForRegion(locality);
      if (!lang || lang === "en") return;
      // Don't suggest a language that already matches the browser default.
      const browser = (navigator.language || "en").toLowerCase().split("-")[0];
      if (browser === lang) return;
      setSuggestion(lang);
    }, 800);
    return () => clearTimeout(t);
  }, [locality?.country, locality?.state]);

  const accept = () => {
    if (!suggestion) return;
    try { localStorage.setItem(PREF_LANG_KEY, suggestion); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent(PREF_LANG_EVENT, { detail: { code: suggestion } })); } catch { /* ignore */ }
    try { localStorage.setItem(DISMISS_KEY, "accepted"); } catch { /* ignore */ }
    setSuggestion(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "dismissed"); } catch { /* ignore */ }
    setSuggestion(null);
  };

  if (!suggestion) return null;
  const native = nativeNameFor(suggestion);

  return (
    <div
      data-testid="lang-auto-suggest"
      className="fixed z-[80] right-4 bottom-4 md:right-6 md:bottom-6 max-w-xs rounded-xl border shadow-lg p-4 pr-8 animate-fadeIn"
      style={{
        background: "var(--wofy-paper, #FDFBF7)",
        borderColor: "var(--wofy-primary, #B25D38)",
        boxShadow: "0 8px 24px rgba(44, 38, 32, 0.15)",
      }}
      role="dialog"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 opacity-50 hover:opacity-100"
        aria-label="Dismiss language suggestion"
        data-testid="lang-auto-suggest-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-2 mb-2">
        <Languages className="w-4 h-4 mt-0.5" style={{ color: "var(--wofy-primary)" }} />
        <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--wofy-primary)" }}>
          Read in your language?
        </div>
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--wofy-text)" }}>
        We noticed you're near {locality?.state || locality?.country}. Would you like content translated to{" "}
        <span className="font-semibold" dir="auto">{native}</span>?
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={accept}
          className="px-3 py-1.5 rounded-full text-xs font-semibold"
          style={{ background: "var(--wofy-primary, #B25D38)", color: "white" }}
          data-testid="lang-auto-suggest-accept"
        >
          <span dir="auto">Yes, use {native}</span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="text-xs opacity-70 hover:opacity-100"
          data-testid="lang-auto-suggest-decline"
        >
          No thanks
        </button>
      </div>
    </div>
  );
}
